import { motion, useReducedMotion } from "motion/react";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

export type PageLoaderProps = {
  className?: string;
  label?: string;
  /** Full viewport-ish height so layout does not jump */
  fullHeight?: boolean;
};

/**
 * Soft Headspace-style loading state between screens / while data hydrates.
 */
export function PageLoader({
  className,
  label = "andrum",
  fullHeight = true,
}: PageLoaderProps) {
  const reduce = useReducedMotion();

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-5 px-8",
        fullHeight && "min-h-[55vh]",
        className
      )}
      role="status"
      aria-live="polite"
      aria-label="Laddar"
    >
      <motion.div
        className="relative flex h-16 w-16 items-center justify-center"
        {...(!reduce
          ? {
              animate: { scale: [1, 1.04, 1] },
              transition: {
                duration: 2.2,
                repeat: Infinity,
                ease: "easeInOut" as const,
              },
            }
          : {})}
      >
        <span className="absolute inset-0 rounded-full bg-primary-soft/80" />
        <span className="absolute inset-2 rounded-full bg-primary/15" />
        <Spinner
          size="lg"
          className="relative text-primary"
          label={label}
        />
      </motion.div>
      <p className="text-sm font-medium tracking-wide text-foreground-muted">
        {label}
      </p>
    </div>
  );
}

/** Soft skeleton blocks for content placeholders */
export function ScreenSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("mx-auto w-full max-w-lg space-y-6 px-6 pt-8", className)}
      aria-hidden
    >
      <div className="space-y-3">
        <div className="h-8 w-2/5 animate-pulse rounded-2xl bg-surface-muted" />
        <div className="h-4 w-4/5 animate-pulse rounded-xl bg-surface-muted/80" />
      </div>
      <div className="h-48 animate-pulse rounded-[28px] bg-surface-raised shadow-sm" />
      <div className="h-40 animate-pulse rounded-[28px] bg-surface-raised shadow-sm" />
      <div className="h-32 animate-pulse rounded-[28px] bg-surface-raised shadow-sm" />
    </div>
  );
}
